import type { JSX } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { mtc, TextColors, TypographyStyles } from '../../../utils/style-helpers'
import {
  tableCellStyles,
  tableHeaderCellDualIconWrapperStyles,
  tableHeaderCellIconStyles,
  tableHeaderCellStyles,
  tableHeaderCellWrapperStyles,
  tableFooterStyles,
  tableRowStyles,
} from './styles'
import { Icon } from '../icon'
import { Text } from '../text'

export const TableHeader = ({
  className,
  ...props
}: JSX.IntrinsicElements['thead']) => <thead className={className} {...props} />

export const TableBody = ({
  className,
  ...props
}: JSX.IntrinsicElements['tbody']) => <tbody className={className} {...props} />

export const TableFooter = ({
  className,
  ...props
}: JSX.IntrinsicElements['tfoot']) => (
  <tfoot className={mtc(tableFooterStyles(), className)} {...props} />
)

export const TableRow = ({
  className,
  ...props
}: JSX.IntrinsicElements['tr']) => (
  <tr className={mtc(tableRowStyles(), className)} {...props} />
)

export enum TableHeaderSort {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
}

export const TableHeaderCell = ({
  className,
  children,
  sorted,
  sortable,
  ...props
}: JSX.IntrinsicElements['th'] & {
  sorted?: TableHeaderSort
  sortable?: boolean
}) => (
  <th
    className={mtc(tableHeaderCellStyles({ sortable }), className)}
    {...props}
  >
    <div className={mtc(tableHeaderCellWrapperStyles())}>
      {/*Auto style input if it's a string*/}
      {typeof children === 'string' ? (
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
          {children}
        </Text>
      ) : (
        children
      )}
      {sorted && (
        <Icon
          icon={
            sorted === TableHeaderSort.ASCENDING
              ? solid('caret-up')
              : solid('caret-down')
          }
          backgroundClassName={mtc(tableHeaderCellIconStyles())}
          color="text-neutral-500"
          className="h-2 w-2"
        />
      )}
      {sortable && !sorted && (
        <div className={mtc(tableHeaderCellDualIconWrapperStyles())}>
          <Icon
            icon={solid('caret-up')}
            backgroundClassName={mtc(
              tableHeaderCellIconStyles({ hasTwoIcons: true })
            )}
            className="h-2 w-2"
            color="text-neutral-500"
          />
          <Icon
            icon={solid('caret-down')}
            backgroundClassName={mtc(
              tableHeaderCellIconStyles({ hasTwoIcons: true })
            )}
            className="h-2 w-2"
            color="text-neutral-500"
          />
        </div>
      )}
    </div>
  </th>
)

export const TableCell = ({
  className,
  children,
  textAlign,
  ...props
}: JSX.IntrinsicElements['td'] & {
  textAlign?: 'center' | 'left' | 'right'
}) => (
  <td className={mtc(tableCellStyles(), className)} {...props}>
    {typeof children === 'string' ? (
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.EMPHASIS}
        textAlign={textAlign}
      >
        {children}
      </Text>
    ) : (
      children
    )}
  </td>
)

export const Table = ({
  className,
  ...props
}: JSX.IntrinsicElements['table']) => (
  <table className={mtc(className, 'w-full')} {...props} />
)

Table.Header = TableHeader
Table.Body = TableBody
Table.Footer = TableFooter
Table.Row = TableRow
Table.HeaderCell = TableHeaderCell
Table.Cell = TableCell
